
// Define utility functions
window.HTMLUpdateUtility =
  window.HTMLUpdateUtility ||
  class HTMLUpdateUtility {
    static viewTransition(
      oldNode,
      newContent,
      preProcessCallbacks = [],
      postProcessCallbacks = []
    ) {
      preProcessCallbacks?.forEach((callback) => callback(newContent));

      const newNodeWrapper = document.createElement("div");
      HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
      const newNode = newNodeWrapper.firstChild;

      // dedupe IDs
      const uniqueKey = Date.now();
      oldNode.querySelectorAll("[id], [form]").forEach((element) => {
        element.id && (element.id = `${element.id}-${uniqueKey}`);
        element.form &&
          element.setAttribute(
            "form",
            `${element.form.getAttribute("id")}-${uniqueKey}`
          );
      });

      oldNode.parentNode.insertBefore(newNode, oldNode);
      oldNode.style.display = "none";

      postProcessCallbacks?.forEach((callback) => callback(newNode));

      setTimeout(() => oldNode.remove(), 500);
    }

    // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    static setInnerHTML(element, html) {
      element.innerHTML = html;
      element.querySelectorAll("script").forEach((oldScriptTag) => {
        const newScriptTag = document.createElement("script");
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(
          document.createTextNode(oldScriptTag.innerHTML)
        );
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }
  };

// global functions
function sofiaFetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

function sofiaDebounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// pub-sub events
const SOFIA_PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  optionValueSelectionChange: "option-value-selection-change",
  variantChange: "variant-change",
  cartError: "cart-error",
};

let sofiaSubscribers = {};

function sofiaSubscribe(eventName, callback) {
  if (sofiaSubscribers[eventName] === undefined) {
    sofiaSubscribers[eventName] = [];
  }

  sofiaSubscribers[eventName] = [...sofiaSubscribers[eventName], callback];

  return function unsubscribe() {
    sofiaSubscribers[eventName] = sofiaSubscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function sofiaPublish(eventName, data) {
  if (sofiaSubscribers[eventName]) {
    const promises = sofiaSubscribers[eventName].map((callback) =>
      callback(data)
    );
    return Promise.all(promises);
  } else {
    return Promise.resolve();
  }
}

// main-product script
if (!customElements.get("sofia-main-product")) {
  customElements.define(
    "sofia-main-product",
    class ProductInfo extends HTMLElement {
      quantitySelector = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantitySelector = this.querySelector(".quantity-selector__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = sofiaSubscribe(
          SOFIA_PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("sofia-main-product:loaded", { bubbles: true })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantitySelector) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        //productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "sofia-main-product[id^='MainProduct']"
            : "sofia-main-product";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("sofia-main-product"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "sofia-variant-picker [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantPicker = html.querySelector("sofia-variant-picker");
        if (variantPicker) {
          HTMLUpdateUtility.viewTransition(
            this.variantPickers,
            variantPicker,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");

          this.updateQuantityRules(this.sectionId, html);

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            "Sold out"
          );

          sofiaPublish(SOFIA_PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        /*this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`
        );*/

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, "Unavailable");

        const selectors = ["price"]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const sofiaGallerySource = this.querySelector("sofia-gallery ul");
        const sofiaGalleryDestination = html.querySelector("sofia-gallery ul");

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const sofiaGallerySourceItems = Array.from(
            sofiaGallerySource.querySelectorAll("li[data-media-id]")
          );
          const sourceSet = new Set(
            sofiaGallerySourceItems.map((item) => item.dataset.mediaId)
          );
          const sourceMap = new Map(
            sofiaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ])
          );
          return [sofiaGallerySourceItems, sourceSet, sourceMap];
        };

        if (sofiaGallerySource && sofiaGalleryDestination) {
          let [sofiaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const sofiaGalleryDestinationItems = Array.from(
            sofiaGalleryDestination.querySelectorAll("li[data-media-id]")
          );
          const destinationSet = new Set(
            sofiaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = sofiaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(sofiaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              sofiaGallerySource.prepend(sofiaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < sofiaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(sofiaGallerySourceItems[i].dataset.mediaId)
            ) {
              sofiaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [sofiaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          sofiaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                sofiaGallerySource.insertBefore(
                  sourceData.item,
                  sofiaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`
                  )
                );

                // refresh source now that it has been modified
                [sofiaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            }
          );
        }

        // set featured media as active in the sofia gallery
        this.querySelector("sofia-gallery")?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          false
        );
      }

      setQuantityBoundries() {
        const data = {
          min: this.quantitySelector.dataset.min
            ? parseInt(this.quantitySelector.dataset.min)
            : 1,
          step: this.quantitySelector.step
            ? parseInt(this.quantitySelector.step)
            : 1,
        };

        let min = data.min;
        this.quantitySelector.min = min;
        this.quantitySelector.value = min;

        sofiaPublish(SOFIA_PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantitySelector) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [".quantity-selector__input"];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity-selector__input") {
            const attributes = ["data-min", "step"];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantPickers() {
        return this.querySelector("sofia-variant-picker");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

// gallery-slider script
if (!customElements.get("sofia-gallery-slider")) {
  customElements.define(
    "sofia-gallery-slider",
    class GallerySliderComponent extends HTMLElement {
      constructor() {
        super();
        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.enableSliderLooping = false;
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');

        if (!this.slider || !this.nextButton) return;

        this.initPages();
        const resizeObserver = new ResizeObserver((entries) =>
          this.initPages()
        );
        resizeObserver.observe(this.slider);

        this.slider.addEventListener("scroll", this.update.bind(this));
        this.prevButton.addEventListener(
          "click",
          this.onButtonClick.bind(this)
        );
        this.nextButton.addEventListener(
          "click",
          this.onButtonClick.bind(this)
        );
      }

      initPages() {
        this.sliderItemsToShow = Array.from(this.sliderItems).filter(
          (element) => element.clientWidth > 0
        );
        if (this.sliderItemsToShow.length < 2) return;
        this.sliderItemOffset =
          this.sliderItemsToShow[1].offsetLeft -
          this.sliderItemsToShow[0].offsetLeft;
        this.slidesPerPage = Math.floor(
          (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
            this.sliderItemOffset
        );
        this.totalPages = this.sliderItemsToShow.length;
        this.update();
      }

      resetPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.initPages();
      }

      update() {
        // Temporarily prevents unneeded updates resulting from variant changes
        // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
        if (!this.slider || !this.nextButton) return;

        const previousPage = this.currentPage;
        this.currentPage =
          Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

        if (this.currentPage != previousPage) {
          this.dispatchEvent(
            new CustomEvent("slideChanged", {
              detail: {
                currentPage: this.currentPage,
                currentElement: this.sliderItemsToShow[this.currentPage - 1],
              },
            })
          );
        }

        if (this.enableSliderLooping) return;

        if (
          this.isSlideVisible(this.sliderItemsToShow[0]) &&
          this.slider.scrollLeft === 0
        ) {
          this.prevButton.setAttribute("disabled", "disabled");
        } else {
          this.prevButton.removeAttribute("disabled");
        }

        if (
          this.isSlideVisible(
            this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
          )
        ) {
          this.nextButton.setAttribute("disabled", "disabled");
        } else {
          this.nextButton.removeAttribute("disabled");
        }
      }

      isSlideVisible(element, offset = 0) {
        const lastVisibleSlide =
          this.slider.clientWidth + this.slider.scrollLeft - offset;
        return (
          element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
          element.offsetLeft >= this.slider.scrollLeft
        );
      }

      onButtonClick(event) {
        event.preventDefault();
        const step = event.currentTarget.dataset.step || 1;
        this.slideScrollPosition =
          event.currentTarget.name === "next"
            ? this.slider.scrollLeft + step * this.sliderItemOffset
            : this.slider.scrollLeft - step * this.sliderItemOffset;
        this.setSlidePosition(this.slideScrollPosition);
      }

      setSlidePosition(position) {
        this.slider.scrollTo({
          left: position,
        });
      }
    }
  );
}

// sofia-gallery script
if (!customElements.get("sofia-gallery")) {
  customElements.define(
    "sofia-gallery",
    class SofiaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          sofiaDebounce(this.onSlideChanged.bind(this), 500)
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
          });
        if (this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("product__media-item--active");
          });
        activeMedia?.classList?.add("product__media-item--active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }
    }
  );
}

//quantity-selector script
if (!customElements.get("sofia-quantity-selector")) {
  customElements.define(
    "sofia-quantity-selector",
    class QuantitySelector extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector("input");
        this.changeEvent = new Event("change", { bubbles: true });
        this.input.addEventListener("change", this.onInputChange.bind(this));
        this.querySelectorAll("button").forEach((button) =>
          button.addEventListener("click", this.onButtonClick.bind(this))
        );
      }

      quantityUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.validateQtyRules();
        this.quantityUpdateUnsubscriber = sofiaSubscribe(
          SOFIA_PUB_SUB_EVENTS.quantityUpdate,
          this.validateQtyRules.bind(this)
        );
      }

      disconnectedCallback() {
        if (this.quantityUpdateUnsubscriber) {
          this.quantityUpdateUnsubscriber();
        }
      }

      onInputChange(event) {
        this.validateQtyRules();
      }

      onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;

        if (event.currentTarget.name === "plus") {
          if (
            parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
            this.input.value == 0
          ) {
            this.input.value = this.input.dataset.min;
          } else {
            this.input.stepUp();
          }
        } else {
          this.input.stepDown();
        }

        if (previousValue !== this.input.value)
          this.input.dispatchEvent(this.changeEvent);

        if (
          this.input.dataset.min === previousValue &&
          event.currentTarget.name === "minus"
        ) {
          this.input.value = parseInt(this.input.min);
        }
      }

      validateQtyRules() {
        const value = parseInt(this.input.value);
        if (this.input.min) {
          const buttonMinus = this.querySelector(
            ".quantity-selector__button[name='minus']"
          );
          buttonMinus.classList.toggle(
            "disabled",
            parseInt(value) <= parseInt(this.input.min)
          );
        }
      }
    }
  );
}

// variant-picker script
if (!customElements.get("sofia-variant-picker")) {
  customElements.define(
    "sofia-variant-picker",
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.addEventListener("change", (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectionMetadata(event);

          sofiaPublish(SOFIA_PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
      }

      updateSelectionMetadata({ target }) {
        const { value, tagName } = target;

        if (tagName === "SELECT" && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute("selected"))
            .removeAttribute("selected");
          target.selectedOptions[0].setAttribute("selected", "selected");
        } else if (tagName === "INPUT" && target.type === "radio") {
          console.log("Selected variant option:", value);
        }
      }

      getInputForEventTarget(target) {
        return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
      }

      get selectedOptionValues() {
        return Array.from(
          this.querySelectorAll(
            'select option[selected], fieldset input[type="radio"]:checked'
          )
        ).map((el) => el.dataset.optionValueId);
      }
    }
  );
}

// bundle-switcher script
(function () {
  const containerControllers = new WeakMap();

  function syncBundleSelectedState(container) {
    container.querySelectorAll("[data-bundle-radio]").forEach((radio) => {
      const bundleItem = radio.closest("[data-bundle-item]");
      if (bundleItem) {
        bundleItem.classList.toggle("bundle-item--selected", radio.checked);
      }
    });
  }

  function initBundleSwitcher() {
    document
      .querySelectorAll("sofia-main-product .bundles")
      .forEach((container) => {
        // Clean up existing listeners
        if (containerControllers.has(container)) {
          containerControllers.get(container).abort();
          containerControllers.delete(container);
        }

        const controller = new AbortController();
        containerControllers.set(container, controller);

        // Sync selected state
        syncBundleSelectedState(container);

        // Handle radio button changes
        container.addEventListener(
          "change",
          (e) => {
            const radio = e.target.closest("[data-bundle-radio]");
            if (radio?.checked) {
              const bundleItems =
                container.querySelectorAll("[data-bundle-item]");
              switchBundle(radio, bundleItems);
            }
          },
          { signal: controller.signal }
        );

        // Handle clicks on bundle items
        container.addEventListener(
          "click",
          (e) => {
            if (e.target.closest("select")) return;

            const bundleItem = e.target.closest("[data-bundle-item]");
            const radio = bundleItem?.querySelector("[data-bundle-radio]");
            if (radio && !radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event("change", { bubbles: true }));
            }
          },
          { signal: controller.signal }
        );
      });
  }

  function switchBundle(radio, bundleItems) {
    const bundleId = radio.value;
    bundleItems.forEach((item) => {
      item.classList.toggle(
        "bundle-item--selected",
        item.dataset.bundleId === bundleId
      );
    });
  }

  function initialize() {
    if (document.readyState !== "loading") {
      initBundleSwitcher();
    }
  }

  // Initialize on DOMContentLoaded
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize);
  } else {
    initialize();
  }

  // Handle page restoration from cache
  window.addEventListener("pageshow", initialize);

  // Handle dynamic content updates
  document.addEventListener("sofia-main-product:loaded", initialize);
})();

// bundle-variant-filter script
(function () {
  const containerControllers = new WeakMap();

  function filterVariants(changedSelect, bundleItem, variantsMap) {
    const bundleId = bundleItem.dataset.bundleId;
    const itemIndex = changedSelect.dataset.itemIndex;
    const allSelects = Array.from(
      bundleItem.querySelectorAll(
        `[data-bundle-variant-select][data-bundle-id="${bundleId}"][data-item-index="${itemIndex}"]`
      )
    ).sort(
      (a, b) =>
        parseInt(a.dataset.optionId || 0) - parseInt(b.dataset.optionId || 0)
    );

    const selectedValues = allSelects.map(
      (s) =>
        s.options[s.selectedIndex]?.getAttribute("data-option-value-id") || null
    );

    allSelects.forEach((select, selectIndex) => {
      const available = new Set();
      Object.values(variantsMap).forEach((variantIds) => {
        const matches = variantIds.every(
          (id, idx) =>
            idx === selectIndex ||
            !selectedValues[idx] ||
            String(id) === String(selectedValues[idx])
        );
        if (matches && variantIds[selectIndex]) {
          available.add(String(variantIds[selectIndex]));
        }
      });

      const currentValue = select.options[select.selectedIndex]?.getAttribute(
        "data-option-value-id"
      );

      Array.from(select.options).forEach((option) => {
        const valueId = option.getAttribute("data-option-value-id");
        if (!valueId) return;
        const isAvailable = available.has(String(valueId));
        option.disabled = !isAvailable;
        option.style.display = isAvailable ? "" : "none";

        if (
          valueId === currentValue &&
          !isAvailable &&
          select !== changedSelect
        ) {
          const firstAvailable = Array.from(select.options).find(
            (opt) =>
              opt.getAttribute("data-option-value-id") &&
              available.has(String(opt.getAttribute("data-option-value-id")))
          );
          if (firstAvailable) {
            select.value = firstAvailable.value;
            select.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
      });
    });
  }

  function init() {
    document
      .querySelectorAll("sofia-main-product .bundles")
      .forEach((container) => {
        if (containerControllers.has(container)) {
          containerControllers.get(container).abort();
        }

        const controller = new AbortController();
        containerControllers.set(container, controller);

        const variantsScript = container
          .closest("sofia-main-product")
          ?.querySelector("[data-product-variants]");
        if (!variantsScript) return;

        let variantsMap;
        try {
          variantsMap = JSON.parse(variantsScript.innerHTML);
        } catch (e) {
          return;
        }

        container.addEventListener(
          "change",
          (e) => {
            const select = e.target.closest("[data-bundle-variant-select]");
            if (!select) return;
            const bundleItem = select.closest("[data-bundle-item]");
            if (bundleItem) {
              filterVariants(select, bundleItem, variantsMap);
            }
          },
          { signal: controller.signal }
        );
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.addEventListener("pageshow", init);
  document.addEventListener("sofia-main-product:loaded", init);
})();

// product-form script
if (!customElements.get("sofia-product-form")) {
  customElements.define(
    "sofia-product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        this.hideErrors = this.dataset.hideErrors === "true";
        this.buyButtonLabel = this.dataset.buyButtonLabel;
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading-overlay__spinner").classList.remove(
          "hidden"
        );

        try {
          // Check if bundles are enabled
          const bundlesContainer =
            this.closest("sofia-main-product")?.querySelector(".bundles");
          const selectedBundleRadio = bundlesContainer?.querySelector(
            "[data-bundle-radio]:checked"
          );

          if (selectedBundleRadio && bundlesContainer) {
            // Handle bundle purchase
            await this.handleBundlePurchase(
              selectedBundleRadio,
              bundlesContainer
            );
          } else {
            // Handle regular single product purchase
            await this.handleRegularPurchase();
          }
        } catch (e) {
          console.error(e);
          this.handleErrorMessage("Failed to add items to cart");
        } finally {
          this.submitButton.classList.remove("loading");
          if (this.cart && this.cart.classList.contains("is-empty"))
            this.cart.classList.remove("is-empty");
          if (!this.error) this.submitButton.removeAttribute("aria-disabled");
          this.querySelector(".loading-overlay__spinner").classList.add(
            "hidden"
          );
        }
      }

      async handleBundlePurchase(selectedBundleRadio, bundlesContainer) {
        const bundleId = selectedBundleRadio.value;
        const bundleItem = bundlesContainer.querySelector(
          `[data-bundle-item][data-bundle-id="${bundleId}"]`
        );
        if (!bundleItem) throw new Error("Bundle item not found");

        const bundleQuantity = parseInt(bundleItem.dataset.bundleQuantity) || 1;
        const defaultVariantId = this.variantIdInput.value;
        const hasVariantSelects =
          bundleItem.querySelector("[data-bundle-variant-select]") !== null;
        const cartItems = [];

        if (!hasVariantSelects) {
          cartItems.push({ id: defaultVariantId, quantity: bundleQuantity });
        } else {
          const productInfo = this.closest("sofia-main-product");
          const variantsMap = JSON.parse(
            productInfo.querySelector("[data-product-variants]").innerHTML
          );

          for (let i = 0; i < bundleQuantity; i++) {
            const selects = bundleItem.querySelectorAll(
              `[data-bundle-variant-select][data-bundle-id="${bundleId}"][data-item-index="${i}"]`
            );
            const optionValueIds = Array.from(selects)
              .sort(
                (a, b) =>
                  parseInt(a.dataset.optionId || 0) -
                  parseInt(b.dataset.optionId || 0)
              )
              .map((s) =>
                s.options[s.selectedIndex]?.getAttribute("data-option-value-id")
              )
              .filter(Boolean)
              .map(String);

            const variantId =
              Object.entries(variantsMap).find(
                ([_, ids]) =>
                  ids.length === optionValueIds.length &&
                  ids.every((id, idx) => String(id) === optionValueIds[idx])
              )?.[0] || defaultVariantId;

            cartItems.push({ id: variantId, quantity: 1 });
          }
        }

        await this.addItemsToCart(cartItems);
      }

      async handleRegularPurchase() {
        const config = sofiaFetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        config.body = formData;

        const response = await fetch(`${sofiaRoutes.cart_add_url}`, config);
        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors);
        }

        window.location = window.sofiaRoutes.cart_url;
      }

      async addItemsToCart(items) {
        const config = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            items: items.map((item) => ({
              id: item.id,
              quantity: item.quantity,
            })),
          }),
        };

        const response = await fetch(`${sofiaRoutes.cart_add_url}`, config);
        const result = await response.json();

        if (result.errors) {
          throw new Error(
            Array.isArray(result.errors)
              ? result.errors.join(", ")
              : result.errors
          );
        }

        window.location = window.sofiaRoutes.cart_url;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = this.buyButtonLabel;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}